import { EmptyState } from '../../components/ui/Layout.tsx';
import { Button } from '../../components/ui/Button.tsx';
import type { Member } from './use-members.ts';

const STATUS_STYLES: Record<Member['status'], string> = {
  active: 'bg-success-tint text-success',
  invited: 'bg-warning-tint text-warning',
  disabled: 'bg-surface text-ink-muted',
};

const STATUS_LABELS: Record<Member['status'], string> = {
  active: 'Active',
  invited: 'Invite pending',
  disabled: 'Disabled',
};

export interface MemberActions {
  currentUserId: string;
  busyId: string | null;
  onRoleChange: (member: Member, role: 'admin' | 'rep') => void;
  onStatusChange: (member: Member, status: 'active' | 'disabled') => void;
  onNewLink: (member: Member) => void;
}

/** Members of a workspace. Read-only when `actions` is omitted (platform owner view). */
export function MemberList({ members, actions }: { members: Member[]; actions?: MemberActions }) {
  if (members.length === 0) return <EmptyState title="No members yet" />;
  return (
    <ul
      className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised"
      aria-label="Members"
    >
      {members.map((m) => {
        const self = actions?.currentUserId === m.userId;
        const busy = actions?.busyId === m.membershipId;
        return (
          <li
            key={m.membershipId}
            className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="member-row"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">
                {m.fullName || m.email}
                {self ? (
                  <span className="ml-1 callout font-normal text-ink-muted">(you)</span>
                ) : null}
              </p>
              <p className="truncate callout text-ink-muted">{m.email}</p>
              <p className="mt-1 flex flex-wrap gap-1 footnote">
                <span className={`rounded-sm px-1.5 py-0.5 font-medium ${STATUS_STYLES[m.status]}`}>
                  {STATUS_LABELS[m.status]}
                </span>
                {actions ? null : (
                  <span className="rounded-sm bg-surface px-1.5 py-0.5 font-medium capitalize text-ink-muted">
                    {m.role}
                  </span>
                )}
              </p>
            </div>
            {actions && !self ? (
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor={`role-${m.membershipId}`}>
                  Role for {m.fullName || m.email}
                </label>
                <select
                  id={`role-${m.membershipId}`}
                  value={m.role}
                  disabled={busy || m.status === 'disabled'}
                  onChange={(e) =>
                    actions.onRoleChange(m, e.target.value === 'admin' ? 'admin' : 'rep')
                  }
                  className="min-h-10 rounded-md border border-control-border bg-surface-raised px-2 callout"
                >
                  <option value="rep">Rep</option>
                  <option value="admin">Admin</option>
                </select>
                {m.status === 'invited' ? (
                  <>
                    <Button variant="secondary" busy={busy} onClick={() => actions.onNewLink(m)}>
                      New link
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => actions.onStatusChange(m, 'disabled')}
                    >
                      Revoke
                    </Button>
                  </>
                ) : m.status === 'active' ? (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => actions.onStatusChange(m, 'disabled')}
                  >
                    Disable
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => actions.onStatusChange(m, 'active')}
                  >
                    Enable
                  </Button>
                )}
              </div>
            ) : actions ? (
              <span className="callout capitalize text-ink-muted">{m.role}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
